import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getProfilePhotoUrl } from '../config';
import { Crown, Shield, MessageCircle, DollarSign, User, Lock } from 'lucide-react';

const AdminTeam = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, super_admin: 0, moderator: 0, support: 0, accountant: 0 });
  const [roles, setRoles] = useState({});
  const [currentPermissions, setCurrentPermissions] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  
  // Modal ajout membre
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [adding, setAdding] = useState(false);

  // Modal modification rôle
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newRole, setNewRole] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [permRes, rolesRes] = await Promise.all([
        api.get('/admin/team/permissions'),
        api.get('/admin/team/roles')
      ]);
      
      setCurrentPermissions(permRes.data.permissions || []);
      setRoles(rolesRes.data.roles || {});

      // Charger les données de l'équipe si on a la permission
      if (permRes.data.permissions?.includes('team_management')) {
        const [membersRes, statsRes, activityRes] = await Promise.all([
          api.get('/admin/team/members'),
          api.get('/admin/team/stats'),
          api.get('/admin/team/activity?limit=20')
        ]);
        setMembers(membersRes.data.members || []);
        setStats(statsRes.data || {});
        setActivityLog(activityRes.data.logs || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Recherche d'utilisateurs
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/admin/team/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.data.users || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleAddMember = async () => {
    if (!selectedUser || !selectedRole) return;
    setAdding(true);
    try {
      await api.post('/admin/team/members', {
        user_id: selectedUser.id,
        admin_role: selectedRole
      });
      setShowAddModal(false);
      setSelectedUser(null);
      setSelectedRole('');
      setSearchQuery('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'ajout');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingMember || !newRole) return;
    try {
      await api.put(`/admin/team/members/${editingMember.id}`, {
        admin_role: newRole
      });
      setShowEditModal(false);
      setEditingMember(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Retirer ${member.nom} de l'équipe d'administration ?`)) return;
    try {
      await api.delete(`/admin/team/members/${member.id}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: 'bg-red-100 text-red-800 border-red-200',
      moderator: 'bg-purple-100 text-purple-800 border-purple-200',
      support: 'bg-blue-100 text-blue-800 border-blue-200',
      accountant: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    return roles[role]?.label || role;
  };

  const getRoleIcon = (role) => {
    const icons = {
      super_admin: <Crown size={16} className="text-amber-500" />,
      moderator: <Shield size={16} className="text-blue-500" />,
      support: <MessageCircle size={16} className="text-green-500" />,
      accountant: <DollarSign size={16} className="text-purple-500" />
    };
    return icons[role] || <User size={16} className="text-gray-500" />;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      'add_team_member': 'A ajouté un membre',
      'update_team_role': 'A modifié un rôle',
      'remove_team_member': 'A retiré un membre'
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Vérifier si l'utilisateur a la permission
  if (!currentPermissions.includes('team_management')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="flex justify-center mb-4">
            <Lock size={64} className="text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès restreint</h2>
          <p className="text-gray-600 mb-6">
            Seuls les Super Admins peuvent gérer l'équipe d'administration.
          </p>
          <Link 
            to="/admin" 
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
          >
            Retour au tableau de bord
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                👥 Équipe d'Administration
              </h1>
              <p className="text-gray-600 mt-1">Gérez les membres et leurs permissions</p>
            </div>
            <div className="flex gap-3">
              <Link 
                to="/admin" 
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
              >
                ← Retour
              </Link>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
              >
                ➕ Ajouter un membre
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total membres</div>
          </div>
          <div className="bg-red-50 rounded-xl shadow p-4 text-center border border-red-100">
            <div className="text-3xl font-bold text-red-600">{stats.super_admin}</div>
            <div className="text-sm text-red-700">👑 Super Admin</div>
          </div>
          <div className="bg-purple-50 rounded-xl shadow p-4 text-center border border-purple-100">
            <div className="text-3xl font-bold text-purple-600">{stats.moderator}</div>
            <div className="text-sm text-purple-700">🛡️ Modérateurs</div>
          </div>
          <div className="bg-blue-50 rounded-xl shadow p-4 text-center border border-blue-100">
            <div className="text-3xl font-bold text-blue-600">{stats.support}</div>
            <div className="text-sm text-blue-700">💬 Support</div>
          </div>
          <div className="bg-green-50 rounded-xl shadow p-4 text-center border border-green-100">
            <div className="text-3xl font-bold text-green-600">{stats.accountant}</div>
            <div className="text-sm text-green-700">💰 Comptables</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Liste des membres */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Membres de l'équipe</h2>
            
            {members.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-8 text-center">
                <div className="text-5xl mb-4">👥</div>
                <p className="text-gray-600">Aucun membre dans l'équipe</p>
              </div>
            ) : (
              <div className="space-y-3">
                {members.map(member => (
                  <div key={member.id} className="bg-white rounded-xl shadow hover:shadow-md transition p-4">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="relative">
                        {member.photo_profil ? (
                          <img 
                            src={getProfilePhotoUrl(member.photo_profil)} 
                            alt={member.nom}
                            className="w-14 h-14 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
                            {member.nom?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <span className="absolute -bottom-1 -right-1 text-lg">
                          {getRoleIcon(member.admin_role)}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{member.nom}</h3>
                        <p className="text-sm text-gray-500 truncate">{member.email}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(member.admin_role)}`}>
                          {getRoleLabel(member.admin_role)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingMember(member);
                            setNewRole(member.admin_role);
                            setShowEditModal(true);
                          }}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                          title="Modifier le rôle"
                        >
                          ✏️
                        </button>
                        {member.admin_role !== 'super_admin' && member.id !== user.id && (
                          <button
                            onClick={() => handleRemoveMember(member)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Retirer de l'équipe"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar - Rôles & Activité */}
          <div className="space-y-6">
            {/* Rôles disponibles */}
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="font-bold text-gray-900 mb-4">📋 Rôles disponibles</h3>
              <div className="space-y-3">
                {Object.entries(roles).map(([key, role]) => (
                  <div key={key} className={`p-3 rounded-lg border ${getRoleColor(key)}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span>{getRoleIcon(key)}</span>
                      <span className="font-semibold">{role.label}</span>
                    </div>
                    <p className="text-xs opacity-80">{role.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Activité récente */}
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="font-bold text-gray-900 mb-4">📜 Activité récente</h3>
              {activityLog.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Aucune activité</p>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {activityLog.map(log => (
                    <div key={log.id} className="text-sm border-b pb-2 last:border-0">
                      <div className="font-medium text-gray-900">{getActionLabel(log.action)}</div>
                      <div className="text-gray-500">par {log.admin_name}</div>
                      <div className="text-xs text-gray-400">{formatDate(log.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajouter un membre */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">➕ Ajouter un membre à l'équipe</h2>
            </div>
            
            <div className="p-6 space-y-5 overflow-y-auto max-h-[60vh]">
              {/* Recherche */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rechercher un utilisateur
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nom ou email..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Résultats de recherche */}
              {searching && (
                <div className="text-center py-4">
                  <div className="animate-spin inline-block w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                </div>
              )}

              {!searching && searchResults.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {searchResults.map(u => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedUser(u)}
                      className={`w-full p-3 rounded-xl border-2 text-left flex items-center gap-3 transition ${
                        selectedUser?.id === u.id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {u.photo_profil ? (
                        <img src={getProfilePhotoUrl(u.photo_profil)} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold">
                          {u.nom?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{u.nom}</div>
                        <div className="text-sm text-gray-500 truncate">{u.email}</div>
                      </div>
                      {selectedUser?.id === u.id && (
                        <span className="text-indigo-600">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Utilisateur sélectionné */}
              {selectedUser && (
                <div className="p-4 bg-indigo-50 rounded-xl">
                  <div className="text-sm text-indigo-600 mb-1">Utilisateur sélectionné</div>
                  <div className="font-semibold text-indigo-900">{selectedUser.nom}</div>
                  <div className="text-sm text-indigo-700">{selectedUser.email}</div>
                </div>
              )}

              {/* Choix du rôle */}
              {selectedUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attribuer un rôle
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {['moderator', 'support', 'accountant'].map(role => (
                      <button
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className={`p-3 rounded-xl border-2 text-left transition ${
                          selectedRole === role 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{getRoleIcon(role)}</span>
                          <span className="font-medium">{roles[role]?.label}</span>
                          {selectedRole === role && <span className="ml-auto text-indigo-600">✓</span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{roles[role]?.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedUser(null);
                  setSelectedRole('');
                  setSearchQuery('');
                }}
                className="px-5 py-2 border border-gray-300 rounded-xl hover:bg-gray-100 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleAddMember}
                disabled={!selectedUser || !selectedRole || adding}
                className="px-5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {adding ? 'Ajout...' : 'Ajouter à l\'équipe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier le rôle */}
      {showEditModal && editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">✏️ Modifier le rôle</h2>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                {editingMember.photo_profil ? (
                  <img src={getProfilePhotoUrl(editingMember.photo_profil)} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold">
                    {editingMember.nom?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-semibold">{editingMember.nom}</div>
                  <div className="text-sm text-gray-500">{editingMember.email}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau rôle
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {['super_admin', 'moderator', 'support', 'accountant'].map(role => (
                    <button
                      key={role}
                      onClick={() => setNewRole(role)}
                      className={`p-3 rounded-xl border-2 text-left transition ${
                        newRole === role 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{getRoleIcon(role)}</span>
                        <span className="font-medium">{roles[role]?.label}</span>
                        {newRole === role && <span className="ml-auto text-indigo-600">✓</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingMember(null);
                }}
                className="px-5 py-2 border border-gray-300 rounded-xl hover:bg-gray-100 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={!newRole || newRole === editingMember.admin_role}
                className="px-5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeam;
